import { useState } from 'react';

import { useToast } from '@codes/ui-kit/components/ui/use-toast';

import type { KeyView } from '@codes/openapi-keys';
import { useDeleteOpenApiKey, useOpenApiKeyList, useRegenerateOpenApiKey } from '@codes/openapi-keys';

type NewlyCreatedKey = {
    apiKey: string;
    name: string;
};

export const useOpenApiKeys = () => {
    const { toast } = useToast();

    const [isFormDialogOpen, setIsFormDialogOpen] = useState(false);
    const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
    const [selectedKey, setSelectedKey] = useState<KeyView | null>(null);
    const [newlyCreatedKey, setNewlyCreatedKey] = useState<NewlyCreatedKey | null>(null);
    const [isSuccessDialogOpen, setIsSuccessDialogOpen] = useState(false);

    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [keyToDelete, setKeyToDelete] = useState<KeyView | null>(null);

    const [isRegenerateDialogOpen, setIsRegenerateDialogOpen] = useState(false);
    const [keyToRegenerate, setKeyToRegenerate] = useState<KeyView | null>(null);

    const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);
    const [keyToValidate, setKeyToValidate] = useState<KeyView | null>(null);

    const keysQuery = useOpenApiKeyList({ params: { view: 'user' } });
    const deleteMutation = useDeleteOpenApiKey();
    const regenerateMutation = useRegenerateOpenApiKey();

    const handleCreate = (): void => {
        setDialogMode('create');
        setSelectedKey(null);
        setIsFormDialogOpen(true);
    };

    const handleEdit = (key: KeyView): void => {
        setDialogMode('edit');
        setSelectedKey(key);
        setIsFormDialogOpen(true);
    };

    const handleDelete = (key: KeyView): void => {
        setKeyToDelete(key);
        setIsDeleteDialogOpen(true);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!keyToDelete?.id) {
            return;
        }

        try {
            await deleteMutation.mutateAsync({ id: keyToDelete.id });
            toast({
                title: 'OpenAPI key deleted successfully',
            });

            setIsDeleteDialogOpen(false);
            setKeyToDelete(null);
        } catch (error) {
            toast({
                title: 'Failed to delete OpenAPI key',
                variant: 'destructive',
            });
        }
    };

    const handleFormSuccess = (key?: NewlyCreatedKey): void => {
        if (key) {
            setNewlyCreatedKey(key);
            setIsSuccessDialogOpen(true);
        }
    };

    const handleRegenerate = (key: KeyView): void => {
        setKeyToRegenerate(key);
        setIsRegenerateDialogOpen(true);
    };

    const confirmRegenerate = async (): Promise<void> => {
        if (!keyToRegenerate?.id) {
            return;
        }

        try {
            const result = await regenerateMutation.mutateAsync({ id: keyToRegenerate.id });
            toast({
                title: 'API key regenerated successfully',
            });

            setIsRegenerateDialogOpen(false);
            setKeyToRegenerate(null);

            setNewlyCreatedKey({
                apiKey: result.apiKey,
                name: result.name || keyToRegenerate.name || 'openapi-key',
            });
            setIsSuccessDialogOpen(true);
        } catch (error) {
            toast({
                title: 'Failed to regenerate API key',
                description: error instanceof Error ? error.message : 'Unknown error',
                variant: 'destructive',
            });
        }
    };

    const handleValidate = (key: KeyView): void => {
        setKeyToValidate(key);
        setIsValidationDialogOpen(true);
    };

    return {
        keysData: keysQuery.data,
        isKeysLoading: keysQuery.isLoading,
        isKeysError: keysQuery.isError,
        keysError: keysQuery.error,
        isFormDialogOpen,
        setIsFormDialogOpen,
        dialogMode,
        selectedKey,
        isSuccessDialogOpen,
        setIsSuccessDialogOpen,
        newlyCreatedKey,
        isDeleteDialogOpen,
        setIsDeleteDialogOpen,
        keyToDelete,
        isDeleting: deleteMutation.isPending,
        isRegenerateDialogOpen,
        setIsRegenerateDialogOpen,
        keyToRegenerate,
        isRegenerating: regenerateMutation.isPending,
        isValidationDialogOpen,
        setIsValidationDialogOpen,
        keyToValidate,
        handleCreate,
        handleEdit,
        handleDelete,
        confirmDelete,
        handleFormSuccess,
        handleRegenerate,
        confirmRegenerate,
        handleValidate,
    };
};
